<script setup lang="ts">
import type { NuxtLinkProps } from '#app'
import type { Crystallize } from '#shared/utils/crystallize-spec'

const props = defineProps<{
  product: ProductCard<Packet | SingleProduct>
  tag?: string
  subtitle?: string
  description?: string
}>()

const route = useRoute()

const description = computed(() => props.description || getGradeRange(props.product))

const url = computed<NuxtLinkProps['to']>(() => ({
  path: `/produktkatalog/${props.product.slug.short}`,
  query: route.query,
}))

const title = computed(() => {
  const preference = [props.product.title, props.product.name]

  if (isPacket(props.product)) {
    // FIXME: Packets currently have a bit weird titles, so for them we want name to take precedence over title. We should fix that.
    preference.reverse()
  }

  return preference.find(truthy)
})

const CLASS_MAP = {
  'productcard-green': 'bg-green-40',
  'productcard-seagreen': 'bg-green-20',
  'productcard-blue': 'bg-blue-50',
  'productcard-darkblue': 'bg-blue-60',
  'productcard-red': 'bg-red-45',
  'productcard-darkred': 'bg-red-60',
  'productcard-purple': 'bg-purple-40',
} as const satisfies Record<Crystallize.SingleProductCardColor, string>
</script>

<template>
  <NuxtLink
    :to="url"
    :class="[product.background && CLASS_MAP[product.background]]"
    :aria-label="`Gå til ${title}`"
    class="group relative z-0 flex h-full min-h-60 flex-col overflow-hidden rounded-lg no-underline transition-all duration-300 ease-in-out hover:contrast-75"
  >
    <span
      v-if="tag"
      class="absolute z-1 rounded-tl-lg rounded-br-lg bg-white px-5 py-2 text-xs text-black-80 uppercase"
      v-text="tag"
    />
    <CrystallizeImage
      class="max-h-32 min-h-32 w-full overflow-hidden"
      :image="product.cover"
      img-class="w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
    />
    <div class="flex flex-1 items-center justify-between gap-legacy-m px-[20px] py-legacy-s text-white">
      <div class="flex flex-col gap-legacy-xs">
        <div class="flex flex-col">
          <span class="text-lg leading-6 font-semibold" v-text="title" />
          <span
            v-if="subtitle"
            class="text-sm font-semibold"
            v-text="subtitle"
          />
        </div>
        <span
          v-if="description"
          class="text-sm"
          v-text="description"
        />
      </div>
      <slot name="icon" />
    </div>
  </NuxtLink>
</template>
